// frontend/src/state/store.ts
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type {
  Instrument,
  Indicator,
  ExpressionConstant,
  ExpressionGraph,
  ExpressionNode,
  RefreshResponse,
  TradeSummaryStats,
  BarType,
  EquityCurveResult,
  SimpleBacktestResult,
  SimpleBacktestRequest,
  BacktestSettings,
  EquityCurveForSetResponse,
} from './types';

export const DEFAULT_BACKTEST_SETTINGS: BacktestSettings = {
  tradeDirection: 'long',
  backtestType: 'simple',
  entryType: 'close',
  exitType: 'close',
  positionSizing: 'fixed_fraction',
  fixedShares: 1,
  fixedAmountDollars: 1000,
  fixedFractionPct: 100,
  initialCapital: 100000,
  allowFractionalShares: true,
  minLotSize: '',
};
import { sExpressionString } from '../utils/sExpressionString';
import { expandExpressionGraph } from '../utils/expandExpressionGraph';

/**
 * When an instrument or indicator is removed, clear any expression slot that referenced it.
 * Sets those slots to null (keeps positions; does not shift operands) and removes orphan nodes.
 */
function clearExpressionReferencesToSeries(
  graph: ExpressionGraph,
  removedSeriesIds: Set<string>
): ExpressionGraph {
  const toDelete = new Set<string>();
  const newExpressions = { ...graph.expressions };

  function isSeriesNodeWithRemovedRef(node: ExpressionNode | undefined): boolean {
    if (!node) return false;
    if (node.type === 'ohlc_series' || node.type === 'indicator_series' || node.type === 'seriesAware') {
      return removedSeriesIds.has(node.seriesId);
    }
    return false;
  }

  for (const [nodeId, node] of Object.entries(graph.expressions)) {
    if (node.type !== 'function') continue;
    const operands = node.operandIds ?? [];
    let changed = false;
    const newOperandIds = operands.map((opId) => {
      if (!opId) return null;
      const child = graph.expressions[opId];
      if (isSeriesNodeWithRemovedRef(child)) {
        toDelete.add(opId);
        changed = true;
        return null;
      }
      return opId;
    });
    if (changed) {
      newExpressions[nodeId] = { ...node, operandIds: newOperandIds };
    }
  }

  if (graph.rootId != null) {
    const rootNode = graph.expressions[graph.rootId];
    if (isSeriesNodeWithRemovedRef(rootNode)) {
      toDelete.add(graph.rootId);
    }
  }

  for (const id of toDelete) {
    delete newExpressions[id];
  }

  const newRootId = graph.rootId != null && toDelete.has(graph.rootId) ? null : graph.rootId;
  return { rootId: newRootId, expressions: newExpressions };
}

/**
 * When a constant is removed, clear any expression slot that referenced it.
 * Sets those slots to null and removes constant nodes.
 */
function clearExpressionReferencesToConstant(
  graph: ExpressionGraph,
  removedConstantId: string
): ExpressionGraph {
  const toDelete = new Set<string>();
  const newExpressions = { ...graph.expressions };

  for (const [nodeId, node] of Object.entries(graph.expressions)) {
    if (node.type !== 'function') continue;
    const operands = node.operandIds ?? [];
    let changed = false;
    const newOperandIds = operands.map((opId) => {
      if (!opId) return null;
      const child = graph.expressions[opId];
      if (child?.type === 'constant' && child.constantId === removedConstantId) {
        toDelete.add(opId);
        changed = true;
        return null;
      }
      return opId;
    });
    if (changed) {
      newExpressions[nodeId] = { ...node, operandIds: newOperandIds };
    }
  }

  if (graph.rootId != null) {
    const rootNode = graph.expressions[graph.rootId];
    if (rootNode?.type === 'constant' && rootNode.constantId === removedConstantId) {
      toDelete.add(graph.rootId);
    }
  }

  for (const id of toDelete) {
    delete newExpressions[id];
  }

  const newRootId = graph.rootId != null && toDelete.has(graph.rootId) ? null : graph.rootId;
  return { rootId: newRootId, expressions: newExpressions };
}

export type OhlcByInstrument = {
  [instrumentId: string]: {
    minDate: string;
    maxDate: string;
    series: Record<string, Record<string, number>>;
  };
};

/** Shared x-axis range for all charts (bar indices). null = not yet set (use full range until primary establishes). */
export type ChartTimeSyncPreset = 'full' | '1Y' | '180D' | '90D' | '30D';

/** Chart identifier for cursor sync; extend when adding new time-aligned charts. */
export type ChartId = 'primary' | 'equity' | 'timeInMarket' | `additional-${string}` | `indicator-${string}`;

type AppState = {
  bar_type: BarType
  timezone: string;
  instruments: Instrument[];
  primaryInstrumentId: string | null;
  indicators: Indicator[];
  expressionGraph: ExpressionGraph;
  exitExpressionGraph: ExpressionGraph;
  constants: ExpressionConstant[];
  ohlc: OhlcByInstrument | null;
  /** Computed indicator series from refresh (id -> date->value or field->(date->value)). */
  indicatorSeries: Record<string, import('./types').IndicatorSeriesData>;
  backtestSummary: {
    constant_hold_trades?: { [bars: number]: TradeSummaryStats };
    exit_on_first_profit_trades?: TradeSummaryStats;
    expression_hold?: TradeSummaryStats;
  } | null;
  backtestResult: SimpleBacktestResult | null;
  backtestEquityCurve: EquityCurveResult | null;
  backtestLoading: boolean;
  /** Last backtest request sent (for equity-curve-for-set when user changes trade set without other changes). */
  lastBacktestRequest: SimpleBacktestRequest | null;
  setLastBacktestRequest: (r: SimpleBacktestRequest | null) => void;
  /** True when expression or backtest params changed since last successful run. */
  backtestParamsDirtySinceRun: boolean;
  setBacktestParamsDirtySinceRun: (dirty: boolean) => void;
  ohlcLoading: boolean;
  /** Error message from last OHLC/refresh failure; cleared when a new fetch starts or succeeds. */
  ohlcError: string | null;

  /** Synced x range (bar indices) for all charts. Primary establishes on load; all charts apply. */
  chartTimeSync: { xMin: number | null; xMax: number | null };
  chartTimeSyncPreset: ChartTimeSyncPreset;
  setChartTimeRange: (xMin: number, xMax: number) => void;
  setChartTimeSyncPreset: (preset: ChartTimeSyncPreset) => void;
  /** Visible time window (Unix s) for cursor alignment. Primary sets when x range changes. */
  chartVisibleTimeRange: { minTs: number; maxTs: number } | null;
  setChartVisibleTimeRange: (minTs: number, maxTs: number) => void;

  /** Synced cursor: timestamp (Unix s) of bar under mouse; null when not over any chart. Charts resolve to local index. */
  chartCursorTimestamp: number | null;
  setChartCursorTimestamp: (ts: number | null) => void;
  /** Fraction (0-1) of visible x range under cursor; non-hovered charts use this so vertical lines align. */
  chartCursorFraction: number | null;
  setChartCursorFraction: (f: number | null) => void;
  /** Which chart has the pointer (hovered chart shows horizontal crosshair; others show only vertical at same fraction). */
  chartHoveredBy: ChartId | null;
  setChartHoveredBy: (id: ChartId | null) => void;

  /** Scale value (e.g. price) at cursor; used for crosshair label. */
  chartCursorScaleValue: number | null;
  setChartCursorScaleValue: (v: number | null) => void;
  /** Top (px) of cursor in primary chart for aligning crosshairs. */
  chartCursorTop: number | null;
  setChartCursorTop: (top: number | null) => void;
  /** Primary chart plot area width (px). */
  primaryChartPlotWidth: number | null;
  setPrimaryChartPlotWidth: (w: number | null) => void;

  /** Primary chart full canvas width (px). Other charts use this so rect.width matches and crosshairs align. */
  primaryChartFullWidth: number | null;
  setPrimaryChartFullWidth: (w: number | null) => void;

  /** Temporary config when adding a series node from the chart (e.g. drag-drop). Cleared after updateSeriesNode. */
  seriesConfig: { field: string; offset: number } | null;
  /** Whether the series being added is OHLC (instrument) vs indicator. */
  isOHLC: boolean;

  /** Selected backtest trade set: 'exit' = exit on first profit, or bars count as string (e.g. '5'). Synced with BacktestTradeList dropdown. */
  selectedTradeSetKey: string;
  setSelectedTradeSetKey: (key: string) => void;
  /** Index of selected trade within the current trade set; null = none. Cleared when trade set changes. */
  selectedTradeIndex: number | null;
  setSelectedTradeIndex: (i: number | null) => void;

  /** Primary chart: display adjusted (true) or unadjusted (false) series. Independent of instrument adjusted (backtest). */
  primaryChartDisplayAdjusted: boolean;
  setPrimaryChartDisplayAdjusted: (b: boolean) => void;
  /** Additional charts: per-instrument display adjusted. Missing key = use that instrument's adjusted. */
  additionalChartDisplayAdjusted: Record<string, boolean>;
  setAdditionalChartDisplayAdjusted: (instrumentId: string, b: boolean) => void;

  /** Backtest Trades card: show adjusted or unadjusted prices in the table. */
  backtestTradePricesView: 'adjusted' | 'unadjusted';
  setBacktestTradePricesView: (view: 'adjusted' | 'unadjusted') => void;

  /** Backtest form settings (position sizing, entry/exit, etc.) for save/load. */
  backtestSettings: BacktestSettings;
  setBacktestSettings: (s: Partial<BacktestSettings>) => void;
  /** Current saved backtest (set when user saves or loads). Enables "Create duplicate" and avoids overwrite warning on re-save. */
  currentSavedBacktestId: string | null;
  currentSavedBacktestName: string | null;
  currentSavedBacktestGroupLabel: string | null;
  setCurrentSavedBacktest: (id: string | null, name: string | null, groupLabel?: string | null) => void;

  setBacktestLoading: (value: boolean) => void;
  setOhlcLoading: (value: boolean) => void;
  setOhlcError: (message: string | null) => void;

  addInstrument: (symbol: string, adjusted: boolean, options?: { setAsPrimary?: boolean }) => string;
  updateInstrument: (id: string, updates: Partial<Instrument>) => void;
  removeInstrument: (id: string) => void;

  addIndicator: (indicator: Omit<Indicator, 'id'>) => string;
  updateIndicator: (id: string, updates: Partial<Omit<Indicator, 'id'>>) => void;
  removeIndicator: (id: string) => void;

  setExpressionGraph: (
    graphOrUpdater: ExpressionGraph | ((prev: ExpressionGraph) => ExpressionGraph)
  ) => void;
  setExitExpressionGraph: (
    graphOrUpdater: ExpressionGraph | ((prev: ExpressionGraph) => ExpressionGraph)
  ) => void;
  updateExpressionNode: (node: ExpressionNode) => void;
  removeExpressionNode: (id: string) => void;

  addConstant: (constant: Omit<ExpressionConstant, 'id'>) => string;
  updateConstant: (id: string, updates: Partial<Omit<ExpressionConstant, 'id'>>) => void;
  removeConstant: (id: string) => void;

  loadFromRefreshResponse: (response: RefreshResponse) => void;
  /** Apply equity-curve-for-set response: update curve and merge sized trades for the selected set into result. */
  applyEquityCurveForSetResponse: (response: EquityCurveForSetResponse) => void;
  updateSeriesNode: (seriesId: string, targetSlotId: string | null) => void;
  reset: () => void;
  /** Reset only selected parts; at least one of symbol, indicators, backtest should be true. */
  resetParts: (opts: { symbol?: boolean; indicators?: boolean; backtest?: boolean }) => void;
  /** Increment to signal Saved backtests list should refetch (e.g. after save/load from card). */
  backtestListVersion: number;
  incrementBacktestListVersion: () => void;
};

export const useAppStore = create<AppState>((set, get) => ({
  timezone: 'America/New_York',
  bar_type: 'day',
  instruments: [],
  primaryInstrumentId: null,
  indicators: [],
  backtestListVersion: 0,
  expressionGraph: {
    rootId: null,
    expressions: {},
  },
  exitExpressionGraph: {
    rootId: null,
    expressions: {},
  },
  constants: [],
  ohlc: null,
  indicatorSeries: {},
  backtestSummary: null,
  backtestResult: null,
  backtestEquityCurve: null,
  backtestLoading: false,
  lastBacktestRequest: null,
  setLastBacktestRequest: (r) => set({ lastBacktestRequest: r }),
  backtestParamsDirtySinceRun: false,
  setBacktestParamsDirtySinceRun: (dirty) => set({ backtestParamsDirtySinceRun: dirty }),
  ohlcLoading: false,
  ohlcError: null,

  chartTimeSync: { xMin: null, xMax: null } as { xMin: number | null; xMax: number | null },
  chartTimeSyncPreset: 'full',
  setChartTimeRange: (xMin, xMax) => set({ chartTimeSync: { xMin, xMax } }),
  setChartTimeSyncPreset: (preset) => set({ chartTimeSyncPreset: preset }),
  chartVisibleTimeRange: null,
  setChartVisibleTimeRange: (minTs, maxTs) =>
    set({ chartVisibleTimeRange: { minTs, maxTs } }),

  chartCursorTimestamp: null,
  setChartCursorTimestamp: (ts) => set({ chartCursorTimestamp: ts }),
  chartCursorScaleValue: null,
  seriesConfig: null,
  isOHLC: false,
  setChartCursorScaleValue: (v: number | null) => set({ chartCursorScaleValue: v }),
  chartCursorFraction: null,
  setChartCursorFraction: (f) => set({ chartCursorFraction: f }),
  chartHoveredBy: null,
  setChartHoveredBy: (id) => set({ chartHoveredBy: id }),
  chartCursorTop: null,
  setChartCursorTop: (top: number | null) => set({ chartCursorTop: top }),

  primaryChartPlotWidth: null,
  setPrimaryChartPlotWidth: (w: number | null) => set({ primaryChartPlotWidth: w }),
  primaryChartFullWidth: null,
  setPrimaryChartFullWidth: (w) => set({ primaryChartFullWidth: w }),

  selectedTradeSetKey: 'exit',
  setSelectedTradeSetKey: (key) =>
    set({ selectedTradeSetKey: key, selectedTradeIndex: null }),
  selectedTradeIndex: null,
  setSelectedTradeIndex: (i) => set({ selectedTradeIndex: i }),

  primaryChartDisplayAdjusted: true,
  setPrimaryChartDisplayAdjusted: (b) => set({ primaryChartDisplayAdjusted: b }),
  additionalChartDisplayAdjusted: {},
  setAdditionalChartDisplayAdjusted: (instrumentId, b) =>
    set((state) => ({
      additionalChartDisplayAdjusted: {
        ...state.additionalChartDisplayAdjusted,
        [instrumentId]: b,
      },
    })),

  backtestTradePricesView: 'unadjusted',
  setBacktestTradePricesView: (view) => set({ backtestTradePricesView: view }),

  backtestSettings: DEFAULT_BACKTEST_SETTINGS,
  setBacktestSettings: (s) =>
    set((state) => ({
      backtestSettings: { ...state.backtestSettings, ...s },
      backtestParamsDirtySinceRun: true,
    })),
  currentSavedBacktestId: null,
  currentSavedBacktestName: null,
  currentSavedBacktestGroupLabel: null,
  setCurrentSavedBacktest: (id, name, groupLabel) =>
    set({
      currentSavedBacktestId: id,
      currentSavedBacktestName: name,
      currentSavedBacktestGroupLabel: id == null ? null : (groupLabel ?? null),
    }),

  setBacktestLoading: (value) => set({ backtestLoading: value }),
  setOhlcLoading: (value) => set({ ohlcLoading: value }),
  setOhlcError: (message) => set({ ohlcError: message }),

  reset: () => set(() => ({
    timezone: 'America/New_York',
    bar_type: 'day',
    instruments: [],
    primaryInstrumentId: null,
    indicators: [],
    expressionGraph: {
      rootId: null,
      expressions: {},
    },
    exitExpressionGraph: {
      rootId: null,
      expressions: {},
    },
    constants: [],
    ohlc: null,
    indicatorSeries: {},
    backtestSummary: null,
    backtestResult: null,
    backtestEquityCurve: null,
    backtestLoading: false,
    ohlcLoading: false,
    ohlcError: null,
    backtestSettings: DEFAULT_BACKTEST_SETTINGS,
    currentSavedBacktestId: null,
    currentSavedBacktestName: null,
    currentSavedBacktestGroupLabel: null,
    chartTimeSync: { xMin: null, xMax: null },
    chartTimeSyncPreset: 'full',
    chartVisibleTimeRange: null,
    chartCursorTimestamp: null,
    chartCursorFraction: null,
    chartHoveredBy: null,
    primaryChartFullWidth: null,
    selectedTradeSetKey: 'exit',
    selectedTradeIndex: null,
  })),

  incrementBacktestListVersion: () =>
    set((s) => ({ backtestListVersion: s.backtestListVersion + 1 })),

  resetParts: (opts) => {
    const { symbol, indicators, backtest } = opts;
    if (!symbol && !indicators && !backtest) return;
    const state = get();
    const removedSeriesIds = new Set<string>();
    if (symbol) state.instruments.forEach((i) => removedSeriesIds.add(i.id));
    if (indicators) state.indicators.forEach((i) => removedSeriesIds.add(i.id));
    let nextGraph = state.expressionGraph;
    let nextExitGraph = state.exitExpressionGraph;
    if (backtest) {
      nextGraph = { rootId: null, expressions: {} };
      nextExitGraph = { rootId: null, expressions: {} };
    } else if (removedSeriesIds.size > 0) {
      nextGraph = clearExpressionReferencesToSeries(state.expressionGraph, removedSeriesIds);
      nextExitGraph = clearExpressionReferencesToSeries(state.exitExpressionGraph, removedSeriesIds);
    }
    set({
      ...(symbol ? { instruments: [] as Instrument[], primaryInstrumentId: null } : {}),
      ...(indicators ? { indicators: [] as Indicator[], indicatorSeries: {} } : {}),
      ...(backtest
        ? {
            expressionGraph: nextGraph,
            exitExpressionGraph: nextExitGraph,
            constants: [] as ExpressionConstant[],
            backtestSettings: DEFAULT_BACKTEST_SETTINGS,
            backtestSummary: null,
            backtestResult: null,
            backtestEquityCurve: null,
            currentSavedBacktestId: null,
            currentSavedBacktestName: null,
            currentSavedBacktestGroupLabel: null,
          }
        : { expressionGraph: nextGraph, exitExpressionGraph: nextExitGraph }),
    });
  },

  addInstrument: (symbol, adjusted, options) => {
    const id = uuidv4();
    const newInstrument = { id, symbol, adjusted };

    set((state) => {
      const isFirst = state.instruments.length === 0;
      const setAsPrimary = options?.setAsPrimary !== false;
      const nextPrimary =
        setAsPrimary && isFirst
          ? id
          : state.primaryInstrumentId;
      return {
        instruments: [...state.instruments, newInstrument],
        primaryInstrumentId: nextPrimary,
        backtestParamsDirtySinceRun: true,
      };
    });

    return id;
  },

  updateInstrument: (id, updates) => {
    set((state) => {
      const nextInstruments = state.instruments.map((instr) =>
        instr.id === id ? { ...instr, ...updates } : instr
      );
      // When symbol changes, clear that instrument's OHLC so useFetchOhlcWhenNeeded refetches
      let nextOhlc = state.ohlc;
      if (updates.symbol !== undefined && state.ohlc?.[id]) {
        const { [id]: _removed, ...rest } = state.ohlc;
        nextOhlc = Object.keys(rest).length > 0 ? rest : null;
      }
      return {
        instruments: nextInstruments,
        ...(nextOhlc !== state.ohlc && { ohlc: nextOhlc }),
        backtestParamsDirtySinceRun: true,
      };
    });
  },

  removeInstrument: (id) => {
    set((state) => {
      const indicatorIdsToRemove = state.indicators
        .filter((ind) => ind.instrumentId === id)
        .map((ind) => ind.id);
      const removedSeriesIds = new Set<string>([id, ...indicatorIdsToRemove]);
      const nextGraph = clearExpressionReferencesToSeries(
        state.expressionGraph,
        removedSeriesIds
      );
      const nextExitGraph = clearExpressionReferencesToSeries(
        state.exitExpressionGraph,
        removedSeriesIds
      );
      const nextInstruments = state.instruments.filter((i) => i.id !== id);
      const wasPrimary = state.primaryInstrumentId === id;
      const nextPrimary = wasPrimary
        ? (nextInstruments[0]?.id ?? null)
        : state.primaryInstrumentId;
      const nextIndicators = state.indicators.filter(
        (ind) => ind.instrumentId !== id
      );
      return {
        instruments: nextInstruments,
        primaryInstrumentId: nextPrimary,
        indicators: nextIndicators,
        expressionGraph: nextGraph,
        exitExpressionGraph: nextExitGraph,
        backtestParamsDirtySinceRun: true,
      };
    });
  },

  addIndicator: (indicator) => {
    const id = uuidv4();
    const newIndicator = { ...indicator, id };
    set((state) => ({
      indicators: [...state.indicators, newIndicator],
    }));
    return id;
  },

  updateIndicator: (id, updates) => {
    set((state) => ({
      indicators: state.indicators.map((i) =>
        i.id === id ? { ...i, ...updates } : i
      ),
    }));
  },

  removeIndicator: (id) => {
    set((state) => {
      const removedSeriesIds = new Set<string>([id]);
      const nextGraph = clearExpressionReferencesToSeries(
        state.expressionGraph,
        removedSeriesIds
      );
      const nextExitGraph = clearExpressionReferencesToSeries(
        state.exitExpressionGraph,
        removedSeriesIds
      );
      return {
        indicators: state.indicators.filter((i) => i.id !== id),
        expressionGraph: nextGraph,
        exitExpressionGraph: nextExitGraph,
      };
    });
  },

  setExpressionGraph: (graphOrUpdater) =>
    set((state) => {
      const prev = state.expressionGraph;
      const next =
        typeof graphOrUpdater === 'function'
          ? graphOrUpdater(prev)
          : graphOrUpdater;

      // Log s-expression for expanded graph (matches backend; seriesAware params visible)
      try {
        const expanded = expandExpressionGraph(next, state.constants);
        console.log('[S-Expression]', sExpressionString(expanded, state.indicators, state.instruments, state.constants));
      } catch {
        console.log('[S-Expression]', sExpressionString(next, state.indicators, state.instruments, state.constants));
      }

      // This is the ONLY thing that changed from original — we now spread state
      return {
        ...state,               // ← THIS WAS MISSING BEFORE
        expressionGraph: next,
        backtestParamsDirtySinceRun: true,
      };
    }),

  setExitExpressionGraph: (graphOrUpdater) =>
    set((state) => {
      const prev = state.exitExpressionGraph;
      const next =
        typeof graphOrUpdater === 'function'
          ? graphOrUpdater(prev)
          : graphOrUpdater;
      return { ...state, exitExpressionGraph: next, backtestParamsDirtySinceRun: true };
    }),

  updateExpressionNode: (node) =>
    set((state) => {
      const updatedGraph: ExpressionGraph = {
        ...state.expressionGraph,
        expressions: {
          ...state.expressionGraph.expressions,
          [node.id]: node,
        },
      };

      try {
        const expanded = expandExpressionGraph(updatedGraph, state.constants);
        console.log('[S-Expression]', sExpressionString(expanded, state.indicators, state.instruments, state.constants));
      } catch {
        console.log('[S-Expression]', sExpressionString(updatedGraph, state.indicators, state.instruments, state.constants));
      }
      return { ...state, expressionGraph: updatedGraph, backtestParamsDirtySinceRun: true };
    }),

  removeExpressionNode: (id) =>
    set((state) => {
      const { [id]: _, ...rest } = state.expressionGraph.expressions;
      const newRoot = state.expressionGraph.rootId === id ? null : state.expressionGraph.rootId;
      const updatedGraph: ExpressionGraph = {
        rootId: newRoot,
        expressions: rest,
      };

      try {
        const expanded = expandExpressionGraph(updatedGraph, state.constants);
        console.log('[S-Expression]', sExpressionString(expanded, state.indicators, state.instruments, state.constants));
      } catch {
        console.log('[S-Expression]', sExpressionString(updatedGraph, state.indicators, state.instruments, state.constants));
      }
      return { ...state, expressionGraph: updatedGraph, backtestParamsDirtySinceRun: true };
    }),

  addConstant: (constant) => {
    const id = uuidv4();
    const newConstant: ExpressionConstant = { ...constant, id };
    set((state) => ({
      constants: [...state.constants, newConstant],
      backtestParamsDirtySinceRun: true,
    }));
    return id;
  },

  updateConstant: (id, updates) =>
    set((state) => {
      const constant = state.constants.find((c) => c.id === id);
      if (!constant) return state;
      const updated: ExpressionConstant = { ...constant, ...updates, id };
      return {
        constants: state.constants.map((c) => (c.id === id ? updated : c)),
        backtestParamsDirtySinceRun: true,
      };
    }),

  removeConstant: (id) =>
    set((state) => {
      const nextGraph = clearExpressionReferencesToConstant(state.expressionGraph, id);
      const nextExitGraph = clearExpressionReferencesToConstant(state.exitExpressionGraph, id);
      const nextConstants = state.constants.filter((c) => c.id !== id);
      return {
        constants: nextConstants,
        expressionGraph: nextGraph,
        exitExpressionGraph: nextExitGraph,
        backtestParamsDirtySinceRun: true,
      };
    }),

  loadFromRefreshResponse: (response: RefreshResponse) => {
    const nextState: Partial<AppState> = {};

    if (response.bar_type) {
      nextState.bar_type = response.bar_type;
    }

    if (response.instruments) {
      nextState.instruments = response.instruments;
    }

    if (response.primaryInstrumentId) {
      nextState.primaryInstrumentId = response.primaryInstrumentId;
    }

    if (response.indicatorConfigs) {
      nextState.indicators = response.indicatorConfigs;
    }

    if (response.backtest?.summary) {
      nextState.backtestSummary = response.backtest.summary;
    }

    if (response.backtest?.result) {
      nextState.backtestResult = response.backtest.result;
    }

    if (response.backtest?.equity_curve != null) {
      nextState.backtestEquityCurve = response.backtest.equity_curve;
    }

    if (response.ohlc) {
      nextState.ohlc = response.ohlc;
    }

    if (response.indicators && typeof response.indicators === 'object' && !Array.isArray(response.indicators)) {
      nextState.indicatorSeries = response.indicators;
      console.log('[loadFromRefreshResponse] indicatorSeries set, keys:', Object.keys(response.indicators));
    }

    if (response.instruments && response.primaryInstrumentId) {
      const primary = response.instruments.find(
        (i) => i.id === response.primaryInstrumentId
      );
      nextState.primaryChartDisplayAdjusted = primary?.adjusted ?? true;
      nextState.additionalChartDisplayAdjusted = Object.fromEntries(
        response.instruments
          .filter((i) => i.id !== response.primaryInstrumentId)
          .map((i) => [i.id, i.adjusted])
      );
    }

    nextState.backtestLoading = false;
    nextState.ohlcLoading = false;
    nextState.ohlcError = null;

    console.log('[loadFromRefreshResponse] applying:', nextState);
    set((state) => ({ ...state, ...nextState }));
  },

  applyEquityCurveForSetResponse: (response: EquityCurveForSetResponse) => {
    set((state) => {
      if (!state.backtestResult) return state;
      const key = response.selectedTradeSet;
      const result = { ...state.backtestResult };
      if (key === 'exit') {
        result.exit_on_first_profit_trades = response.trades;
      } else {
        const k = Number(key);
        if (!Number.isNaN(k)) {
          result.constant_hold_trades = { ...result.constant_hold_trades, [k]: response.trades };
        }
      }
      return {
        ...state,
        backtestEquityCurve: response.equity_curve,
        backtestResult: result,
      };
    });
  },

  updateSeriesNode: (seriesId: string, targetSlotId: string | null) => set((state) => {
    if (!state.seriesConfig) return state;
    const { field, offset } = state.seriesConfig;
    const nodeId = `s-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNode: ExpressionNode = state.isOHLC
      ? { id: nodeId, type: 'ohlc_series', seriesId, field: field as 'open' | 'high' | 'low' | 'close' | 'volume' | 'date', offset }
      : { id: nodeId, type: 'indicator_series', seriesId, offset, field };
   
    let updatedGraph = { ...state.expressionGraph };
    if (targetSlotId) {
      const match = targetSlotId.match(/^(.+)-slot-(\d+)$/);
      if (match) {
        const parentId = match[1];
        const operandIndex = parseInt(match[2], 10);
        const parent = state.expressionGraph.expressions[parentId];
        if (parent && parent.type === 'function') {
          const updatedOperandIds = parent.operandIds.map((id, idx) =>
            idx === operandIndex ? nodeId : (id === undefined || id === null ? nodeId : id)
          );
          const updatedParent: ExpressionNode = {
            ...parent,
            operandIds: updatedOperandIds,
          };
          updatedGraph = {
            ...state.expressionGraph,
            expressions: {
              ...state.expressionGraph.expressions,
              [nodeId]: newNode,
              [parentId]: updatedParent,
            } as Record<string, ExpressionNode>,
          };
        }
      }
    } else if (!state.expressionGraph.rootId) {
      updatedGraph = {
        ...state.expressionGraph,
        rootId: nodeId,
        expressions: {
          ...state.expressionGraph.expressions,
          [nodeId]: newNode,
        } as Record<string, ExpressionNode>,
      };
    }
    return { expressionGraph: updatedGraph, seriesConfig: null };
  }),
}));